<template>
    <section class="qr-search" data-test="qr-directory">
        <header class="qr-search__header">
            <router-link
                :to="{ name: QR_ROUTES.HOME, params: { entranceId: route.params.entranceId } }"
                class="qr-search__back"
                :aria-label="$t('qr.search.back')"
                data-test="qr-search-back"
            >
                <Icon name="arrow-left" class="w-6 h-6" />
            </router-link>
            <h1 class="qr-search__title">{{ $t('qr.search.title') }}</h1>
        </header>

        <p class="qr-search__notice">{{ $t('qr.search.notice') }}</p>

        <label class="qr-search__field">
            <span class="qr-search__label">{{ $t('qr.search.label') }}</span>
            <input
                ref="input"
                :value="query"
                class="qr-search__input"
                type="search"
                autocomplete="off"
                autocapitalize="words"
                enterkeyhint="search"
                data-test="qr-search-input"
                @input="onInput"
            />
        </label>

        <ul v-if="results.length" class="qr-search__results" data-test="qr-search-results">
            <li v-for="resident in results" :key="resident.id">
                <button
                    type="button"
                    class="qr-search__item"
                    data-test="qr-search-item"
                    :aria-label="$t('qr.search.video_call', { name: resident.displayName })"
                    @click="startVideoCall(resident)"
                >
                    <span class="qr-search__avatar" :style="{ backgroundColor: badgeColorFor(resident.displayName) }" aria-hidden="true">
                        {{ initialOf(resident.displayName) }}
                    </span>
                    <span class="qr-search__name">{{ resident.displayName }}</span>
                    <Icon name="qr-camera" class="qr-search__call w-6 h-6" data-test="qr-search-item-call" />
                </button>
            </li>
        </ul>
        <div v-if="results.length && !last" ref="sentinel" class="qr-search__sentinel" data-test="qr-search-sentinel"></div>

        <p class="qr-search__status" role="status" aria-live="polite">
            <template v-if="error">
                {{ $t('qr.search.error') }}
                <button type="button" class="qr-search__retry" data-test="qr-search-retry" @click="retry">
                    {{ $t('qr.search.retry') }}
                </button>
            </template>
            <template v-else-if="loading">{{ $t('qr.search.searching') }}</template>
            <template v-else-if="hasSearched && !results.length">{{ $t('qr.search.no_results') }}</template>
            <template v-else-if="query.trim() && !hasSearched">{{ $t('qr.search.min_chars', { count: MIN_SEARCH_LENGTH }) }}</template>
        </p>
    </section>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import Icon from '@/components/ui/Icon';
import { normalizeSearchQuery, useResidentSearch } from '../../composables/qr/useResidentSearch';
import { MIN_SEARCH_LENGTH, QR_ROUTES } from '../../constants/qr';
import { useQrVisitorContext } from '../../composables/qr/useQrVisitorContext';

const BADGE_COLORS = ['#f07e6e', '#8662bf', '#528add', '#719c30', '#dd8001', '#c67744'];

const route = useRoute();
const router = useRouter();
const store = useStore();
const visitor = useQrVisitorContext();
const { query, results, loading, error, last, hasSearched, loadMore, retry } = useResidentSearch({
    intercomId: computed(() => route.params.entranceId),
    coordinates: computed(() => visitor.coordinates.value),
    initialQuery: store.getters['qr/searchQuery'],
    onOutsideGeofence: error => {
        visitor.invalidateVerification(error);
        router.replace({ name: QR_ROUTES.LOCATION, params: { entranceId: route.params.entranceId } });
    },
});
const input = ref(null);
const sentinel = ref(null);
let observer = null;

const initialOf = name => name.trim().charAt(0).toUpperCase();

function badgeColorFor(name) {
    const hash = [...name].reduce((sum, char) => sum + char.charCodeAt(0), 0);
    return BADGE_COLORS[hash % BADGE_COLORS.length];
}

function onInput(event) {
    const element = event.target;
    const raw = element.value;
    const next = normalizeSearchQuery(raw);
    if (next !== raw) {
        // Vue skips the DOM update when the model value is unchanged, so write it back and keep the caret in place
        const caret = normalizeSearchQuery(raw.slice(0, element.selectionStart ?? raw.length)).length;
        element.value = next;
        try {
            element.setSelectionRange(caret, caret);
        } catch {
            // Some input types do not support selection
        }
    }
    query.value = next;
}

// Kept in the store so the query survives the call and is restored on return
watch(query, value => store.dispatch('qr/setSearchQuery', value));

function startVideoCall(resident) {
    store.dispatch('qr/selectResident', {
        id: resident.id,
        displayName: resident.displayName,
        badgeColor: badgeColorFor(resident.displayName),
    });
    router.push({
        name: QR_ROUTES.CALL,
        params: { entranceId: route.params.entranceId, residentId: resident.id },
    });
}

onMounted(() => {
    input.value?.focus();
    if (typeof IntersectionObserver === 'undefined') return;
    observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) loadMore();
    }, { rootMargin: '200px' });
    if (sentinel.value) observer.observe(sentinel.value);
});

watch(sentinel, (element, previous) => {
    if (previous) observer?.unobserve(previous);
    if (element) observer?.observe(element);
});

// Re-observing fires a fresh callback when the sentinel is still on screen after a short page
watch(loading, isLoading => {
    if (isLoading || !sentinel.value || !observer) return;
    observer.unobserve(sentinel.value);
    observer.observe(sentinel.value);
});

onBeforeUnmount(() => observer?.disconnect());
</script>

<style scoped>
.qr-search {
    max-width: 480px;
    margin: 0 auto;
    padding: 24px 20px calc(24px + env(safe-area-inset-bottom));
    font-family: sofia-pro, Helvetica, Arial, sans-serif;
    color: #101010;
}

.qr-search__header {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 24px;
}

.qr-search__back {
    display: flex;
    color: #101010;
}

.qr-search__title {
    font-size: 26px;
    font-weight: 700;
    color: #8662bf;
}

.qr-search__notice {
    margin-bottom: 20px;
    font-family: sofia-pro, Helvetica, Arial, sans-serif;
    font-size: 14px;
    line-height: 20px;
    font-weight: 300;
    color: #696969;
}

.qr-search__field {
    display: block;
    margin-bottom: 16px;
}

.qr-search__label {
    display: block;
    margin-bottom: 6px;
    font-family: sofia-pro, Helvetica, Arial, sans-serif;
    font-size: 14px;
    font-weight: 300;
    color: #696969;
}

.qr-search__input {
    width: 100%;
    height: 52px;
    padding: 0 16px;
    border: 1px solid #101010;
    border-radius: 8px;
    font-size: 20px;
    outline: none;
}

.qr-search__input:focus {
    border-color: #8662bf;
    box-shadow: 0 0 0 2px #e7d9ff;
}

.qr-search__results {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.qr-search__item:focus-visible {
    outline: 2px solid #8662bf;
    outline-offset: 2px;
}

.qr-search__item {
    display: flex;
    align-items: center;
    gap: 16px;
    width: 100%;
    padding: 12px 16px;
    border: 1px solid #ececec;
    border-radius: 12px;
    background: #f7f7f7;
    text-align: left;
}

.qr-search__avatar {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    color: #fff;
    font-weight: 700;
}

.qr-search__name {
    flex: 1;
    min-width: 0;
    font-size: 17px;
    font-weight: 700;
    overflow-wrap: anywhere;
}

.qr-search__call {
    flex-shrink: 0;
    color: #8a8a8a;
}

.qr-search__sentinel {
    height: 1px;
}

.qr-search__status {
    margin-top: 16px;
    font-family: sofia-pro, Helvetica, Arial, sans-serif;
    font-size: 14px;
    font-weight: 300;
    color: #696969;
    text-align: center;
}

.qr-search__retry {
    display: block;
    margin: 8px auto 0;
    font-weight: 700;
    color: #8662bf;
}
</style>
